import React from 'react';
import { AlertCircle, Info, CheckCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';

interface QualityWarningProps {
  qualityScore: number;
  confidenceLevel: 'high' | 'medium' | 'low';
  qualityIssues?: Array<{ type: string; timestamp?: string }>;
  onReupload?: () => void;
  onAccept?: () => void;
  documentName: string;
}

const QualityWarning: React.FC<QualityWarningProps> = ({
  qualityScore,
  confidenceLevel,
  qualityIssues = [],
  onReupload,
  onAccept,
  documentName
}) => {
  // Determine severity level
  const getSeverity = () => {
    if (qualityScore >= 80) return 'success';
    if (qualityScore >= 60) return 'warning';
    return 'error';
  };

  const severity = getSeverity();

  const getIcon = () => {
    if (severity === 'success') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (severity === 'warning') return <Info className="h-5 w-5 text-yellow-600" />;
    return <AlertCircle className="h-5 w-5 text-red-600" />;
  };

  const getBackgroundColor = () => {
    if (severity === 'success') return 'bg-green-50 border-green-200';
    if (severity === 'warning') return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getTextColor = () => {
    if (severity === 'success') return 'text-green-900';
    if (severity === 'warning') return 'text-yellow-900';
    return 'text-red-900';
  };

  const getMessage = () => {
    if (severity === 'success') {
      return `Excellent! Your ${documentName} was extracted with high quality.`;
    }
    if (severity === 'warning') {
      return `Your ${documentName} was extracted but the quality is moderate. Some information may be missing or incomplete.`;
    }
    return `Your ${documentName} has low extraction quality. Important information may be missing.`;
  };

  const getRecommendation = () => {
    if (severity === 'success') return null;
    if (severity === 'warning') {
      return 'We recommend re-uploading a text-based PDF for better results, but you can proceed with the current extraction.';
    }
    return 'We strongly recommend re-uploading your document as a text-based PDF to ensure accurate career intelligence analysis.';
  };

  return (
    <div className={`rounded-lg border p-4 ${getBackgroundColor()}`}>
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <h4 className={`font-semibold mb-1 ${getTextColor()}`}>
            Extraction Quality: {qualityScore}/100 ({confidenceLevel} confidence)
          </h4>
          <p className={`text-sm mb-2 ${getTextColor()}`}>
            {getMessage()}
          </p>

          {getRecommendation() && (
            <p className={`text-sm mb-3 ${getTextColor()}`}>
              {getRecommendation()}
            </p>
          )}

          {qualityIssues.length > 0 && (
            <div className="mb-3">
              <p className={`text-xs font-medium mb-1 ${getTextColor()}`}>
                Quality Issues Detected:
              </p>
              <ul className={`text-xs list-disc list-inside ${getTextColor()}`}>
                {qualityIssues.slice(0, 5).map((issue, idx) => (
                  <li key={idx}>{issue.type.replace(/_/g, ' ')}</li>
                ))}
              </ul>
            </div>
          )}

          {severity !== 'success' && (
            <div className="flex gap-2">
              {onReupload && (
                <Button
                  size="sm"
                  onClick={onReupload}
                  className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Re-upload Better PDF
                </Button>
              )}
              {onAccept && severity === 'warning' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onAccept}
                  className="text-gray-600"
                >
                  Proceed Anyway
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QualityWarning;
